import { ActionAfterViewObserver, type TLifecycleHookEvent } from '@cian/react-utils';
import { ModalFullscreen } from '@cian/ui-kit';
import * as React from 'react';
import { VIEWED_TIMEOUT } from 'shared/mf-infrastructure/constants/viewedBlock';
import { IInfrastructureProps } from 'shared/mf-infrastructure/containers/InfrastructureContainer/types';
import { ITAInfrastructureData, TTAInfrastructureType } from 'shared/mf-infrastructure/types/transportAccessibility';
import { getYandexMapPreview } from 'shared/mf-infrastructure/utils/getYandexMapPreview';
import { toGeoObjects } from 'shared/mf-infrastructure/utils/toGeoObjects';

import { useAnalyticsParams, useFullscreenProps, useGeoObjectBalloon, useRouteButtonProps } from './hooks';
import { InfrastructureMapContainer, InfrastructureMapTypes } from './parts';
import { trackInfrastructureShow } from './tracking';
import {
  InfrastructureLayout,
  RouteButton,
  MobilePreview,
  CloseModalButton,
  MobileModalLayout,
} from '../../components/Infrastructure';

export const InfrastructureMobileContainer: React.FC<IInfrastructureProps> = ({
  apiKey,
  suggestApiKey,
  mapRef,
  isMapLoaded,
  currentTypes,
  onGeoObjectsFetch,
  infrastructureByYearMap,
  centeredCoordinates,
  infrastructureTypes,
  setInfrastructureType,
  handleMapLoaded,
  controls,
  suggestPosition,
}) => {
  const analyticsParams = useAnalyticsParams({});
  const { isFullscreenOpen, handleFullscreenExit, handleFullscreenEnter } = useFullscreenProps(analyticsParams);
  const previewSrc = getYandexMapPreview(centeredCoordinates);

  const { startBuildingRoute, isRouteMode, handleRouteModeEnter, handleRouteModeExit, handleRouteSuccess } =
    useRouteButtonProps({
      mapRef,
      centeredCoordinates,
      analyticsParams,
    });
  const { onBalloonHtmlFirstFetch, onBalloonOpen } = useGeoObjectBalloon({
    infrastructureByYearMap,
    transportAccessibilityInfrastructure: new Map<TTAInfrastructureType, ITAInfrastructureData>(),
    startBuildingRoute,
    mapService: null,
    mapApi: mapRef.current,
    mainPinCoordinates: centeredCoordinates,
  });

  const handleModalExit = () => {
    handleFullscreenExit();

    if (isRouteMode) {
      handleRouteModeExit();
    }
  };

  const handleMapShown = React.useCallback(
    () => trackInfrastructureShow(analyticsParams.blockType, analyticsParams.profile),
    [analyticsParams.blockType, analyticsParams.profile],
  );
  const viewedTimerRef = React.useRef<number | undefined>(undefined);

  const clearViewedTimer = React.useCallback(() => {
    window.clearTimeout(viewedTimerRef.current);
    viewedTimerRef.current = undefined;
  }, []);

  const handleMapInViewport = React.useCallback(
    ({ subscribeState }: TLifecycleHookEvent) => {
      clearViewedTimer();
      viewedTimerRef.current = window.setTimeout(() => {
        handleMapShown();
        subscribeState.setSubscribeUnavailable();
        viewedTimerRef.current = undefined;
      }, VIEWED_TIMEOUT);
    },
    [clearViewedTimer, handleMapShown],
  );

  React.useEffect(() => clearViewedTimer, [clearViewedTimer, handleMapInViewport]);

  React.useEffect(() => {
    if (isMapLoaded) {
      const geoObjects = toGeoObjects(infrastructureByYearMap, new Date().getFullYear());
      const geoObjectsByType = geoObjects.filter(item => currentTypes.includes(item.name));

      mapRef.current?.removeAllGeoObjects();
      mapRef.current?.addGeoObjects(geoObjectsByType);
    }
  }, [currentTypes]);

  return (
    <>
      <ActionAfterViewObserver<HTMLDivElement>
        deps={[clearViewedTimer, handleMapInViewport]}
        onInViewport={handleMapInViewport}
        onOutViewport={clearViewedTimer}
      >
        {({ elementRef }) => (
          <div ref={elementRef}>
            <InfrastructureLayout isMobile>
              <MobilePreview previewSrc={previewSrc} onClick={handleFullscreenEnter} />
            </InfrastructureLayout>
          </div>
        )}
      </ActionAfterViewObserver>

      <ModalFullscreen open={isFullscreenOpen} theme="white" onClose={handleFullscreenExit}>
        <MobileModalLayout>
          <InfrastructureMapContainer
            apiKey={apiKey}
            suggestApiKey={suggestApiKey}
            mapRef={mapRef}
            controls={controls}
            onGeoObjectsFetch={onGeoObjectsFetch}
            onBalloonHtmlFetch={onBalloonHtmlFirstFetch}
            onMapLoaded={handleMapLoaded}
            onBalloonOpen={onBalloonOpen}
            centeredCoordinates={centeredCoordinates}
            geoObjectNames={currentTypes}
            suggestPosition={suggestPosition}
            onRoutingStart={handleRouteModeEnter}
            onRoutingEnd={handleRouteModeExit}
            onRouteBuildingSuccess={handleRouteSuccess}
          />

          <CloseModalButton key="close-button" onClick={handleModalExit} />

          {!isRouteMode && (
            <>
              <RouteButton key="route-button" onClick={() => startBuildingRoute()} isMobile fixed />

              <InfrastructureMapTypes
                analyticsParams={analyticsParams}
                key="infrastructure-map-types"
                types={infrastructureTypes}
                currentTypes={currentTypes}
                onTypesChange={setInfrastructureType}
                fixed
              />
            </>
          )}
        </MobileModalLayout>
      </ModalFullscreen>
    </>
  );
};
